import { useState, useContext, useEffect, useRef } from "react";
import { LanguageContext } from "../../../../context/LanguageContext";
import { Box } from "@mui/material";
import { AmountBox, AppAmountBoxWrapper } from "../../../common";
import { AppPagination } from "../../../../Libraries";
import { FilterNonIdeal } from "../../../common/NewNonIdeal";
import ItemList from "./ItemList";
import ExpenseListTableHeader from "./ExpenseListTableHeader";
import { formatNum } from "../../../../oscar-pos-core/constants";
import { useNavigate, useLocation } from "react-router-dom";
import { buildFiltersConfig, getCategoriesByType, mergeCashbookCategories } from "../../../../utils";
import { useExpenseList } from "../../../../hooks/useExpenseList";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import {
  DEFAULT_DATE_FILTERS_EXPORT,
  EMIONBOARDINGSTATUS,
  ExpenseCategoryList,
  ITEMS_PER_PAGE,
  NEW_SORT,
  TABLE_FILTERS,
  VERIFIED,
} from "../../../../constants";
import AppTableFilters from "../../../common/AppTableFilters";
import { useFiltersHandler } from "../../../../hooks/useFilterHandler";
import useDownlaodPdf from "../../../../hooks/useDownlaodPdf";
import withObservables from "@nozbe/with-observables";
import { of } from "rxjs";
import { useWalletContext } from "../../../../context/WalletContext";
import { useSnackbar } from "../../../../context/SnackbarContext";

const ExpenseList = ({
  business,
  user,
  cashbook_categories,
  allCustomers,
  initialQuickExpense,
}) => {
  const [I18n] = useContext(LanguageContext);

  const [page, setPage] = useState(0);
  const [selectedContactId, setSelectedContactId] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const justClosedDropdownRef = useRef(false);
  const { showSnackbar } = useSnackbar();

  const [invoiceCategories, setInvoiceCategories] =
    useState(ExpenseCategoryList);
  const { onDownload, downloadLoading } = useDownlaodPdf(user);
  const navigate = useNavigate();
  const location = useLocation();
  const { accountInfo, fetchAccountInfo, isAccountLoading } =
    useWalletContext();

  const {
    filters,
    appliedChips,
    handleFilterChange,
    resetFilters,
    handleRemoveChip,
    isResetFilter,
    setIsResetFilter,
  } = useFiltersHandler(undefined, I18n);

  const {
    expenseList,
    handleExpense,
    totalExpenseFilterCount,
    totalExpenseAmount,
    totalPaid,
    totalUnPaid,
    isLoading,
  } = useExpenseList(initialQuickExpense, ITEMS_PER_PAGE);

  const handleInitialCall = () => {
    setPage(0);
    handleExpense({
      business,
      filters,
      limit: ITEMS_PER_PAGE,
      offset: 0,
    });
  };

  useEffect(() => {
    handleInitialCall();
  }, [filters]);

  useEffect(() => {
    if (cashbook_categories && cashbook_categories?.length) {
      let allCategories = mergeCashbookCategories(cashbook_categories);
      setInvoiceCategories(allCategories);
    }
  }, [cashbook_categories]);

  const handleGoDetail = (item) => {
    navigate(`/Accounts/AccountDetail/${item.id}`);
  };

  const handleDownloadReport = (startDate, endDate) => {
    triggerLogEvent(analytics, "exp_report_download");
    let paramsForReport = {
      name: "invoice_statement.pdf",
      template_name: "accountreport_new.hbs",
      business_id: business.id,
      start_date: startDate || "",
      end_date: endDate || "",
      type: "expense",
      invoice_type: "expense",
      pdf: true,
    };

    onDownload(paramsForReport, () => {}, navigate);
  };

  const handleSendMoney = async (expense, customerDetail) => {
    setSelectedId(expense?.id);

    let info = accountInfo;

    if (!info && user?.phone_number) {
      info = await fetchAccountInfo(navigate);
    }

    if (!info) {
      return;
    }

    const isVerifiedAccountForWallet =
      info?.emi_onboarding_status === EMIONBOARDINGSTATUS.VERIFIED &&
      info?.verified === VERIFIED.VERIFIED;

    if (isVerifiedAccountForWallet) {
      triggerLogEvent(analytics, "expense_send_money");
      const amountToSend = expense.amount - (expense.advance_amount || 0);
      const invoiceUpdatePayload = {
        invoiceId: expense.id,
        amountToSend,
        customerId: customerDetail?.id || null,
      };
      navigate("/UCash/SendMoney", {
        state: { invoiceUpdatePayload, backPath: location.pathname },
      });
      return;
    }

    let lastVisited = localStorage.getItem("last_visited");
    navigate(lastVisited ? lastVisited : "/WalletOnboarding");
    showSnackbar({
      message:
        "Your account is not verified for wallet. Please verify to use this feature.",
      severity: "error",
    });
  };

  return (
    <Box className="AccountWrapper">
      <AppTableFilters
        filters={TABLE_FILTERS}
        onFilterChange={handleFilterChange}
        appliedChips={appliedChips}
        filterState={filters}
        onRemoveChip={(chip) => handleRemoveChip(chip)}
        filtersConfig={buildFiltersConfig(TABLE_FILTERS, getCategoriesByType("business_expense"), null, null, I18n.search_by_contact || "Search by contact", null, null, null, null, I18n)}
        isResetFilter={isResetFilter}
        setIsResetFilter={setIsResetFilter}
        exportConfig={{
          onExport: handleDownloadReport,
          loading: downloadLoading,
          dateFilters: DEFAULT_DATE_FILTERS_EXPORT,
          applyBtnText: I18n.export,
          spinnerColor: "black",
        }}
      />

      <AppAmountBoxWrapper>
        <AmountBox
          data={[
            {
              amount: formatNum(totalExpenseAmount),
              title: I18n.total_expense,
              id: "total_expense",
              rsLabel: true,
            },
            {
              amount: formatNum(totalPaid),
              title: I18n.total_paid_expenses,
              id: "total_paid_expenses",
              rsLabel: true,
            },
            {
              amount: formatNum(totalUnPaid),
              title: I18n.total_unpaid_expenses,
              id: "total_unpaid_expenses",
              rsLabel: true,
              amountParaTextClassname: "foundationGreenColor",
            },
          ]}
        />
      </AppAmountBoxWrapper>

      <ExpenseListTableHeader
        isAscending={filters.sort !== "Newest"}
        isAscendingStatus={filters.sort !== NEW_SORT.unpaid}
        disabled={!expenseList?.length}
        handleSort={(tab) => {
          handleFilterChange(
            "sort",
            tab == "status"
              ? filters.sort == NEW_SORT.unpaid
                ? NEW_SORT.blank
                : NEW_SORT.unpaid
              : filters.sort == "Oldest"
                ? "Newest"
                : "Oldest",
          );
        }}
      />
      <Box
        className="accountListBodyWarpper generic-scrollbar"
        id="scrollableDiv"
      >
        {expenseList?.length ? (
          <AppPagination
            data={{
              handleNext: (e) => {
                if (expenseList.length < totalExpenseFilterCount) {
                  setPage(page + 1);
                  handleExpense({
                    business,
                    filters,
                    limit: ITEMS_PER_PAGE,
                    offset: (page + 1) * ITEMS_PER_PAGE,
                    isCalculateTotal: false,
                  });
                }
              },
              pageLoad: true,
              count: totalExpenseFilterCount,
              list: expenseList,
              scrollableTarget: "scrollableDiv",
            }}
          >
            {expenseList.map((item, index) => (
              <ItemList
                key={index}
                index={index}
                expense={item}
                onSelect={() => handleGoDetail(item)}
                contactList={allCustomers}
                handleSendMoney={handleSendMoney}
                business={business}
                selectedId={selectedId}
                selectedContactId={selectedContactId}
                setSelectedContactId={setSelectedContactId}
                justClosedDropdownRef={justClosedDropdownRef}
                isAccountLoading={isAccountLoading}
                onEdit={() => {
                  triggerLogEvent(analytics, "exp-edit");
                  navigate(`/Expense/ExpenseForm/Edit/${item.id}`);
                }}
                refetchList={() => {
                  handleInitialCall();
                }}
              />
            ))}
          </AppPagination>
        ) : (
          !isLoading && (
            <Box className="h-100">
              <FilterNonIdeal
                title={I18n.no_transactions_matching_those_filters}
                subTitle={I18n.try_adjusting_filters}
                subTitleTextOnClick={() => resetFilters()}
                subTitleText={I18n.reset_small}
              />
            </Box>
          )
        )}
      </Box>
    </Box>
  );
};

const enhance = withObservables(["business"], ({ business }) => {
  return {
    cashbook_categories: business
      ? business.cashbook_categories.observe()
      : of([]),
    allCustomers: business ? business?.getAllCustomers.observe() : of([]),
    initialQuickExpense: business
      ? business.getExpense(undefined, undefined, "").observe()
      : of([]),
  };
});

export default enhance(ExpenseList);
